import {
  Appstore,
  Facebook,
  Instagram,
  LinkedIn,
  Playstore,
  Tiktok,
  Twitter,
  Youtube,
} from "@/components/bnp-icons";
import Image from "next/image";
import Link from "next/link";
import { CookieSettingsButton } from "@/components/cookies/CookieSettingsButton";

interface FooterLink {
  text: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface FooterColumn {
  sections: FooterSection[];
}

const Footer: React.FC = () => {
  const footerData: FooterColumn[] = [
    {
      sections: [
        {
          title: "QUICK LINKS",
          links: [
            { text: "Become a realtor", href: "/auth/sign-up" },
            { text: "Advertise/Promote property", href: "/contact" },
          ],
        },

        {
          title: "MANAGED DEVELOPMENTS",
          links: [
            { text: "Available development", href: "/investments" },
            {
              text: "List development offerings",
              href: "/user/dashboard/properties/my-developments",
            },
            // { text: "Properties for investments", href: "/investments" },
          ],
        },

        {
          title: "MANAGED PROPERTIES",
          links: [
            {
              text: "Properties for Rent",
              href: "/managed-properties?listingType=rent",
            },
            {
              text: "Properties for Sale",
              href: "/managed-properties?listingType=sale",
            },
            {
              text: "Properties for Shortlets",
              href: "/managed-properties?listingType=shortlet",
            },
            // { text: "Properties for investments", href: "/investments" },
          ],
        },
      ],
    },
    {
      sections: [
        {
          title: "BUY, RENT AND SELL",
          links: [
            {
              text: "Flat for rent in Abuja",
              href: "/properties?state=Abuja&city=Central+Business+District&listingType=rent",
            },
            {
              text: "Houses for rent in Abuja",
              href: "/properties?state=Abuja&city=Central+Business+District&listingType=rent&propertyType=house",
            },
            {
              text: "Houses for sale in Abuja",
              href: "/properties?state=Abuja&city=Central+Business+District&listingType=buy&propertyType=house",
            },
            {
              text: "Land for sale in Abuja",
              href: "/properties?state=Abuja&city=Central+Business+District&listingType=land",
            },
            {
              text: "Mini flats for rent in Abuja",
              href: "/properties?state=Abuja&city=Central+Business+District&listingType=rent&propertyType=apartment",
            },
            {
              text: "Self contain for rent in Abuja",
              href: "/properties?state=Abuja&city=Central+Business+District&bedroomCount=1&bathroomCount=1&listingType=rent",
            },
            {
              text: "Flat for rent in Lagos",
              href: "/properties?state=Lagos&city=Ikeja&listingType=rent",
            },
            {
              text: "Houses for rent in Lagos",
              href: "/properties?state=Lagos&city=Ikeja&listingType=rent&propertyType=house",
            },
            {
              text: "Houses for sale in Lagos",
              href: "/properties?state=Lagos&city=Ikeja&listingType=buy&propertyType=house",
            },
            {
              text: "Land for sale in Lagos",
              href: "/properties?state=Lagos&city=Ikeja&listingType=land",
            },
            {
              text: "Mini flats for rent in Lagos",
              href: "/properties?state=Lagos&city=Ikeja&listingType=rent&propertyType=apartment",
            },
            {
              text: "Self contain for rent in Lagos",
              href: "/properties?state=Lagos&city=Ikeja&bedroomCount=1&bathroomCount=1&listingType=rent",
            },
          ],
        },
      ],
    },
    {
      sections: [
        {
          title: "ABOUT",
          links: [
            // { text: "Company", href: "/company" },
            // { text: "How it works", href: "/how-it-works" },
            { text: "Contact", href: "/contact" },
            // { text: "Developments", href: "/investments" },
          ],
        },
        {
          title: "TERMS & PRIVACY",
          links: [
            // { text: "FAQs", href: "/faqs" },
            { text: "Terms of Service", href: "/terms-and-conditions" },
            { text: "Privacy Policy", href: "/privacy-policy" },
            { text: "Cookie Policy", href: "/cookie-policy" },
          ],
        },
      ],
    },
  ];

  const socialLinks = [
    {
      icon: <Facebook />,
      href: "https://www.facebook.com/findnigeriaproperties",
    },
    {
      icon: <Instagram />,
      href: "https://www.instagram.com/theofficialfnp",
    },
    { icon: <Twitter />, href: "https://www.x.com/theofficialfnp" },
    {
      icon: <Tiktok />,
      href: "https://www.tiktok.com/@theofficialfnp",
    },
    {
      icon: <LinkedIn />,
      href: "https://www.linkedin.com/company/officialfnp/?viewAsMember=true",
    },
    {
      icon: <Youtube />,
      href: "https://www.youtube.com/@theofficialfnp",
    },
  ];

  return (
    <footer className="bg-footer-dark py-16 px-8 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-16">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <Link href={"/"}>
                <img
                  src="/fnp-logo-with-credibility-subtext-white.png"
                  alt="FindNigeriaProperties.com"
                  className="h-12 w-auto"
                />
              </Link>
            </div>

            <div className="flex flex-col justify-center items-center mt-[90px] lg:mt-[186px]">
              <p className="text-sm font-medium text-base-white mb-4">
                Our mobile app is coming soon on !
              </p>
              <Playstore />
              <Image
                //@ts-ignore
                src={"/assets/images/apple-store.png"}
                width={163}
                height={50}
                alt={"apple-store-logo"}
              />
            </div>
          </div>

          <div className="">
            <div className="ml-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-32 gap-y-8 w-[100%]">
              {footerData.map((column, columnIndex) => (
                <div key={columnIndex} className="space-y-8">
                  {column.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="space-y-4">
                      <h3 className="text-base-white font-semibold text-sm">
                        {section.title}
                      </h3>
                      <ul className="space-y-2">
                        {section.links.map((link, linkIndex) => (
                          <li key={linkIndex}>
                            <a
                              href={link.href}
                              target="_blank"
                              className="text-base-white hover:text-base-white text-sm transition-colors duration-200"
                            >
                              {link.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-base-white text-sm text-center lg:text-start">
              ©2026 FindNigeriaProperties.com by Freshspring Property
              Development Company Limited &nbsp;|&nbsp; All rights reserved
              &nbsp;|&nbsp; <CookieSettingsButton />
            </div>

            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  className="text-grey-7 hover:text-base-white transition-colors duration-200"
                >
                  <span className="w-6 h-6 flex items-center justify-center">
                    <span className="text-grey-7">{social.icon}</span>
                    <div className="w-5 h-5" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
